import React from 'react';
import axios from 'axios';
import omit from 'lodash/omit';
import { withRouter } from 'react-router-dom';
import { lawsAPIToken } from '~api/laws';
import { toast } from 'react-toastify';
import { ErrorMessage } from '~components/toast';
import { withNamespaces, Trans } from 'react-i18next';
import Input from '~components/input';
import LoadingButton from '~components/loading-button';
import { iconCheckBold } from '~utils/svgIcons';
import { identifyUserAnalytics } from '~utils/analytics';
import '~routes/login-page/style.scss';
import './style.scss';

function ResetForm({ history, logIn, isFetching, isMobile, redirectPath }) {
    const token = React.useMemo(() => {
        const pageParams = new URLSearchParams(document.location.search.substring(1));
        if (pageParams instanceof Object) {
            return pageParams.get('token') || '';
        }
        return '';
    }, []);

    const [isLoading, setLoading] = React.useState(false);

    const [tokenError, setTokenError] = React.useState('');

    const [state, setState] = React.useState({
        password: {
            value: '',
            isInvalid: false,
            error: <Trans i18nKey="validationRules.required-message" />,
        },
        secondpassword: {
            value: '',
            isInvalid: false,
            error: <Trans i18nKey="validationRules.required-message" />,
        },
    });

    const [validationState, setValidationState] = React.useState({
        length: false,
        number: false,
        uppercaseChar: false,
        lowercaseChar: false,
        special: false,
    });

    const validatePassword = React.useCallback((value) => {
        const length = value.length > 7;
        const number = value.search(/[0-9]{1}/gi) !== -1;
        const uppercaseChar = value.search(/[A-Z]{1}/g) !== -1;
        const lowercaseChar = value.search(/[a-z]{1}/g) !== -1;
        const special = value.search(/[$&+,:;=?@#|'<>.^*()%!-]{1}/gi) !== -1;

        setValidationState({
            length,
            number,
            uppercaseChar,
            lowercaseChar,
            special,
        });
    }, []);

    const onChangeHandler = React.useCallback(
        (event) => {
            setState({
                ...state,
                [event.target.name]: {
                    value: event.target.value.trim(),
                    isInvalid: false,
                    error: '',
                },
            });
            if (event.target.name === 'password') {
                validatePassword(event.target.value);
            }
        },
        [state, validatePassword]
    );

    React.useEffect(() => {
        lawsAPIToken()
            .post('/users/check_password_recovery_token/', { token: token })
            .then(() => setTokenError(''))
            .catch((error) => {
                if (!axios.isCancel(error) && error.response.status === 400) {
                    setTokenError('Невалидный токен');
                    return
                }
                if (!axios.isCancel(error)) {
                    setTokenError(error.message);
                }
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onFormSubmit = React.useCallback(
        async (event) => {
            event.preventDefault();
            const { password, secondpassword } = state;

            let isValid = true;
            for (let k in validationState) {
                if (!validationState[k]) {
                    isValid = false;
                    break;
                }
            }

            const isPasswordEmpty = !password.value;
            const isSecondPasswordEmpty = !secondpassword.value;

            const isPasswordsTheSame = password.value === secondpassword.value;

            if (!isValid || !isPasswordsTheSame) {
                setState({
                    password: {
                        value: password.value.trim(),
                        isInvalid: !isValid || isPasswordEmpty ? true : false,
                        error: isPasswordEmpty ? (
                            <Trans i18nKey="validationRules.required-message" />
                        ) : !isValid ? (
                            'Пароль не соответствует политике ИБ ОАО «РЖД»'
                        ) : (
                            ''
                        ),
                    },
                    secondpassword: {
                        value: secondpassword.value.trim(),
                        isInvalid: !isPasswordsTheSame || isSecondPasswordEmpty ? true : false,
                        error: !isPasswordsTheSame ? (
                            'Пароли не совпадают'
                        ) : isSecondPasswordEmpty ? (
                            <Trans i18nKey="validationRules.required-message" />
                        ) : (
                            ''
                        ),
                    },
                });
                return;
            }

            setLoading(true);

            const lawsResponse = await lawsAPIToken()
                .post('/users/reset_password/', { token: token, password: secondpassword.value })
                .then((data) => data)
                .catch((error) => {
                    console.log(error);
                    if (!axios.isCancel(error)) {
                        toast(<ErrorMessage message={error.message} />);
                    }
                    setLoading(false);
                    return false;
                });

            let user = {};
            const pathBeforeLogin = window.location.pathname;
            const responseRedirectPath = lawsResponse.data.redirect_url;
            const isUserPath = pathBeforeLogin.length > 2;
            if (lawsResponse && lawsResponse.data && lawsResponse.data.auth_login_response) {
                const lawsResponseUser = lawsResponse.data.auth_login_response.user || {};
                const group = Array.isArray(lawsResponseUser.groups) ? lawsResponseUser.groups[0] : null;
                const listEcosystemFunctions = group &&
                    Array.isArray(group.ecosystem_functions) && [...group.ecosystem_functions];
                let ecosystemFunctions = {};
                Array.isArray(listEcosystemFunctions) &&
                    listEcosystemFunctions.map((item) => (ecosystemFunctions[item.code] = true));
                const company = group ? group.company : null;
                const listSubdivisions = company ? company.groups : null;
                user = {
                    ...user,
                    ...omit(lawsResponseUser, ['id', 'groups']),
                    group,
                    ecosystem: {
                        functions: ecosystemFunctions,
                        listFunctions: listEcosystemFunctions,
                        company,
                        listSubdivisions: listSubdivisions || null,
                    },
                    lawsUser: {
                        id: lawsResponse.data.auth_login_response.user.id,
                        token: lawsResponse.data.auth_login_response.token,
                        token_expires_at: lawsResponse.data.auth_login_response.token_expires_at,
                    },
                };
                logIn(user);
            }            
            if (user && user.lawsUser && user.lawsUser.id) {
                identifyUserAnalytics(user);
            }
            if (!responseRedirectPath) {
                history.push(isUserPath ? pathBeforeLogin : redirectPath);
            }
            if (responseRedirectPath) {
                window.location.replace(responseRedirectPath);
            }
        },
        [state, token, validationState, logIn, history, redirectPath]
    );

    const { password, secondpassword } = state;

    return (
        <form className="new-password-form">
            <h3 className="login-page__header">{tokenError || 'Сброс пароля'}</h3>
            {!tokenError && (
                <React.Fragment>
                    <p className="new-password-form__description">Новый пароль должен содержать не менее:</p>
                    <ul className="new-password-form__checklist">
                        <li className="new-password-form__check-item">
                            <span className={`new-password-form__check${validationState.length ? ' active' : ''}`}>
                                <i className="new-password-form__check-icon">{iconCheckBold}</i>
                            </span>{' '}
                            8 знаков/символов
                        </li>
                        <li className="new-password-form__check-item">
                            <span className={`new-password-form__check${validationState.number ? ' active' : ''}`}>
                                <i className="new-password-form__check-icon">{iconCheckBold}</i>
                            </span>{' '}
                            1 цифра
                        </li>
                        <li className="new-password-form__check-item">
                            <span
                                className={`new-password-form__check${validationState.uppercaseChar ? ' active' : ''}`}
                            >
                                <i className="new-password-form__check-icon">{iconCheckBold}</i>
                            </span>{' '}
                            1 заглавная
                        </li>
                        <li className="new-password-form__check-item">
                            <span
                                className={`new-password-form__check${validationState.lowercaseChar ? ' active' : ''}`}
                            >
                                <i className="new-password-form__check-icon">{iconCheckBold}</i>
                            </span>{' '}
                            1 строчная
                        </li>
                        <li className="new-password-form__check-item">
                            <span className={`new-password-form__check${validationState.special ? ' active' : ''}`}>
                                <i className="new-password-form__check-icon">{iconCheckBold}</i>
                            </span>{' '}
                            1 спецсимвол
                        </li>
                    </ul>
                    <Input
                        type="password"
                        placeholder="Введите новый пароль"
                        name="password"
                        onChange={onChangeHandler}
                        value={password.value}
                        isError={password.isInvalid}
                        message={password.error}
                    />
                    <Input
                        type="password"
                        placeholder="Повторите новый пароль"
                        name="secondpassword"
                        onChange={onChangeHandler}
                        value={secondpassword.value}
                        isError={secondpassword.isInvalid}
                        message={secondpassword.error}
                    />
                    <LoadingButton
                        type="submit"
                        size={isMobile ? 'small' : 'big'}
                        disabled={isLoading}
                        onClick={onFormSubmit}
                        isLoading={isLoading}
                        isMobile={isMobile}
                        ariaLabel="Восстановить пароль"
                    >
                        Изменить пароль
                    </LoadingButton>
                </React.Fragment>
            )}
        </form>
    );
}

export default withRouter(withNamespaces('translation')(ResetForm));



// WEBPACK FOOTER //
// ./src/routes/login-page/login/new-password-form/index.js